// @owners { team: patients-team }
import { COLORS } from '@alto/design-library-tokens';
import {
  AltoSpinningLoader,
  Body,
  Button,
  Card,
  Column,
  Description,
  H1,
  Icon,
  InlineAlert,
  LgPadding,
  LgSpacing,
  ListItem,
  MdSpacing,
  SecondaryPage,
  SmSpacing,
  Toast,
  ToastContext,
  XsSpacing,
} from '@alto/design-system';
import { useEnterCheckoutFlow } from '@alto/features';
import { useNavigation } from '@alto/navigation';
import { type PhotoIDUploadUser } from '@alto/scriptdash/alto/patient_app/checkout_flow/types/v1/photo_id_upload';
import React, { useCallback, useContext, useEffect, useRef } from 'react';
import { TypescriptUtils } from '~shared/TypescriptUtils';
import { clearUserError, uploadPhotoIDs } from '~shared/actions/users';
import getLoading from '~shared/features/ui/selectors/getLoading';
import { getCurrentUser } from '~shared/features/users/selectors/getCurrentUser';
import getSelectedUser from '~shared/features/users/selectors/getSelectedUser';
import { isUnderAgeEighteen } from '~shared/helpers/date';
import { useAnalytics } from '~shared/hooks';
import { EVENTS } from '~shared/lib/analytics/src/constants';
import { ORIGIN_NAMES } from '~shared/lib/analytics/src/getOrigin';
import { useDispatchShared, useSelectorShared } from '~shared/store';
import { type User } from '~shared/types';
import { PhotoIDImageUploadModule } from './PhotoIDImageUploadModule';

// type guards to differentiate user types
const isUser = (user: User | PhotoIDUploadUser): user is User => {
  return 'first_name' in user;
};
const getUserName = (user: User | PhotoIDUploadUser) => {
  if (isUser(user)) {
    return user.first_name;
  } else {
    return user.name;
  }
};

// eslint-disable-next-line sonarjs/cognitive-complexity
export const PhotoIDUpload = () => {
  const { trackPageView, trackEvent } = useAnalytics();
  const dispatch = useDispatchShared();
  const { goBack, getParam, navigate } = useNavigation<'RouteCheckoutPhotoIdUpload'>();
  const isProfileUpload = getParam('origin') === ORIGIN_NAMES.PROFILE;
  const { photoIDUploadData: data, isLoading } = useEnterCheckoutFlow();
  const { uploadPhotoIDsLoading: loading } = useSelectorShared(getLoading);
  const { addToast } = useContext(ToastContext);
  const currentUser = useSelectorShared(getCurrentUser);
  const selectedUser = useSelectorShared(getSelectedUser);
  // Object of userID and image file to be uploaded to S3
  const [photoIDs, setPhotoIDs] = React.useState<Record<number, File>>({});
  // Object of userID and image URL to be displayed in the UI
  const [photoURLs, setPhotoURLs] = React.useState<Record<number, string>>({});
  // Track blob URLs for memory cleanup
  const blobURLsRef = useRef<Record<number, string>>({});

  let users;
  let hasMinor = false;
  let contextUser: User | null | undefined;

  if (isProfileUpload) {
    // when origin is user profile page, use selected user only
    users = [selectedUser].filter(TypescriptUtils.isPresent);
    hasMinor = users.some((user) => isUnderAgeEighteen(user.date_of_birth || ''));
    contextUser = selectedUser;
  } else {
    // when entering checkout, use current user and users from enter checkout flow data
    users = data?.users ?? [];
    hasMinor = users.some((user) => user.is_minor);
    contextUser = currentUser;
  }

  const hasUploadedAllIDs = Object.keys(photoIDs).length === users.length;
  const onlyNeedPhotoIDForCurrentUser = users.length === 1 && users[0].id === contextUser?.id;
  const userID = contextUser?.id;

  useEffect(() => {
    trackPageView({
      event: EVENTS.UPLOAD_ID_VIEWED,
    });
  }, [trackPageView]);

  // Cleanup all blob URLs on unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      Object.values(blobURLsRef.current).forEach((url) => {
        URL.revokeObjectURL(url);
      });
    };
  }, []);

  const onPressBack = () => {
    goBack();
  };

  const handlePhotoUpload = useCallback(({ userID, file }: { userID: number; file: File }) => {
    // Revoke old blob URL if exists to prevent memory leak
    if (blobURLsRef.current[userID]) {
      URL.revokeObjectURL(blobURLsRef.current[userID]);
    }

    // Create new blob URL
    const newBlobURL = URL.createObjectURL(file);
    blobURLsRef.current[userID] = newBlobURL;

    // Update state
    setPhotoIDs((prev) => ({ ...prev, [userID]: file }));
    setPhotoURLs((prev) => ({ ...prev, [userID]: newBlobURL }));
  }, []);

  const handlePhotoDelete = useCallback((userID: number) => {
    setPhotoIDs((prev) => {
      if (!prev[userID]) {
        return prev;
      }

      // Revoke blob URL to prevent memory leak
      if (blobURLsRef.current[userID]) {
        URL.revokeObjectURL(blobURLsRef.current[userID]);
        blobURLsRef.current = Object.fromEntries(
          Object.entries(blobURLsRef.current).filter(([key]) => Number(key) !== userID),
        );
      }

      const updated = { ...prev };
      // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
      delete updated[userID];
      return updated;
    });

    setPhotoURLs((prev) => {
      if (!prev[userID]) {
        return prev;
      }
      const updated = { ...prev };
      // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
      delete updated[userID];
      return updated;
    });
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!hasUploadedAllIDs) {
      addToast(<Toast variant="error">Upload all required IDs to continue</Toast>);
      return;
    }
    const succeeded = await dispatch(uploadPhotoIDs(photoIDs));

    if (succeeded) {
      addToast(<Toast variant="success">Photo ID has successfully uploaded.</Toast>);
      trackEvent({ event: EVENTS.UPLOAD_ID_SUCCEEDED });

      dispatch(clearUserError());
      const route = window.location.pathname;
      if (isProfileUpload && userID) {
        navigate('RouteUserProfile', { userID });
      } else if (route !== '/checkout') {
        navigate('RouteCheckout');
      }
    } else {
      addToast(<Toast variant="error">Please try again or message support if the problem persists.</Toast>);
      trackEvent({ event: EVENTS.UPLOAD_ID_FAILED });
    }
  }, [hasUploadedAllIDs, dispatch, photoIDs, addToast, trackEvent, isProfileUpload, navigate, userID]);

  return (
    <SecondaryPage
      headerBackgroundColor={COLORS.BACKGROUND_COLORS.GREY_LIGHTEST}
      onDismiss={onPressBack}
      HeaderContent={
        <LgPadding>
          <H1 center>Upload valid government-issued ID</H1>
          <SmSpacing />
          <Body center>
            {isProfileUpload ? '' : 'Your order includes a controlled substance. '}To help us verify your identity
            before preparing your order for pickup or delivery, please upload a valid government-issued ID.
          </Body>
        </LgPadding>
      }
    >
      <Card>
        <LgPadding>
          <Body fontFamily="semibold">Accepted ID types:</Body>
          <Body>• Driver's license</Body>
          <Body>• State-issued identification</Body>
          <Body>• Passport</Body>
          <Body>• Military identification card</Body>
          <SmSpacing />
          <InlineAlert type="warning">
            {hasMinor ? (
              <>
                <Description>For patients under 18, a parent or guardian's ID may be uploaded instead.</Description>
                <XsSpacing />
              </>
            ) : null}

            <Description fontFamily="semibold">Submitting an invalid ID may result in order cancellation.</Description>
          </InlineAlert>
          <LgSpacing />
          {isLoading ? (
            <Column centerHorizontally>
              <LgPadding>
                <AltoSpinningLoader />
              </LgPadding>
            </Column>
          ) : null}
          {onlyNeedPhotoIDForCurrentUser && userID ? (
            <>
              {photoIDs[userID] ? (
                <>
                  <ListItem
                    fullBleed
                    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                    title={isProfileUpload ? `${getUserName(selectedUser!)}'s ID` : undefined}
                    RightContent={
                      <Icon
                        name="trash"
                        accessibilityLabel="Press to delete government issued photo ID"
                      />
                    }
                    onPress={() => {
                      handlePhotoDelete(userID);
                    }}
                  />
                  <MdSpacing />
                </>
              ) : null}
              <PhotoIDImageUploadModule
                userID={userID}
                loading={loading}
                onImageUpload={handlePhotoUpload}
                image={photoURLs[userID]}
              />
            </>
          ) : (
            users.map((user, idx) => (
              <React.Fragment key={user.id}>
                {idx > 0 ? <MdSpacing /> : null}
                <ListItem
                  fullBleed
                  title={`${getUserName(user)}'s ID`}
                  RightContent={
                    photoIDs[user.id] ? (
                      <Icon
                        name="trash"
                        accessibilityLabel="Press to delete government issued photo ID"
                      />
                    ) : undefined
                  }
                  onPress={() => {
                    handlePhotoDelete(user.id);
                  }}
                />
                <MdSpacing />
                <PhotoIDImageUploadModule
                  userID={user.id}
                  loading={loading}
                  onImageUpload={handlePhotoUpload}
                  image={photoURLs[user.id]}
                />
              </React.Fragment>
            ))
          )}
          <LgSpacing />
          <Button
            label={isProfileUpload ? 'Save photo ID' : 'Continue to checkout'}
            onPress={handleSubmit}
            type={hasUploadedAllIDs ? 'primary' : 'secondary'}
            loading={loading}
            disabled={users.length === 0}
          />
          {isProfileUpload ? (
            <Button
              label="Cancel"
              onPress={onPressBack}
              type="tertiary"
            />
          ) : undefined}
        </LgPadding>
      </Card>
    </SecondaryPage>
  );
};
